/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { apiClient } from '@/lib/apiClient';

/** A UI display language available in the settings "Language" picker. */
export interface LanguageDto {
  languageId: number;
  languageCode: string;
  displayString: string;
}

export type ThemeSetting = 'system' | 'light' | 'dark';

/** Backend-owned service-manual disclaimer shown before diagram access. */
export interface DisclaimerContent {
  title: string;
  subtitle: string;
  danger: string;
  bullets: string[];
  reacknowledgementDays: number;
}

/** Enriched user settings from `GET /settings/user/{gmin}` (codes resolved to catalog entries). */
export interface UserSettingsModel {
  languageDisplay: LanguageDto;
  userId: string;
  themeSetting: ThemeSetting;
}

/** Flat user settings returned from the save endpoint. */
export interface UserSettingsDto {
  settingsId: number;
  userId: string;
  languageCode: string;
  themeSetting: ThemeSetting;
}

/** User disclaimer acknowledgement status; `needsAcknowledgement` is server-derived. */
export interface DisclaimerAcknowledgement {
  lastAcknowledgedAt: string | null;
  acknowledgement: boolean;
  needsAcknowledgement: boolean;
}

/** The system of record a profile field belongs to, when one claims it. */
export type ProfileFieldSource = 'account' | 'gdl';

/**
 * Contact details already resolved against Entra and GDL, with `locked` naming the fields those
 * systems own. Anything absent from `locked` is the user's to fill in.
 */
export interface UserProfile {
  userId: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  /** Optional second address, stored in the long-standing `alternate_email` column. */
  secondaryEmail?: string;
  phone?: string;
  /** Optional second number; never supplied by GDL, always the user's own. */
  alternatePhone?: string;
  dealerName?: string;
  address?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  /** ISO 3166-1 alpha-2; the dealership's country is the user's country. */
  country?: string;
  locked?: Partial<Record<keyof UserProfileUpdate, ProfileFieldSource>>;
}

/** Per-user schematic-display preferences (`/api/v1/schematic-properties/user/{gmin}`). */
export interface SchematicProperties {
  userId: string;
  symbolSize: number;
  wireSize: number;
  spacing: number;
  showEngineeringLabels: boolean;
  showWireColors: boolean;
  showCommonViewTypes: boolean;
}

export async function getLanguages(): Promise<LanguageDto[]> {
  const { data } = await apiClient.get<LanguageDto[]>('/ediagnostics/v1/settings/languages');
  return data ?? [];
}

export async function getDisclaimer(): Promise<DisclaimerContent> {
  const { data } = await apiClient.get<DisclaimerContent>('/ediagnostics/v1/settings/disclaimer');
  return data;
}

export async function getUserSettings(gmin: string): Promise<UserSettingsModel> {
  const { data } = await apiClient.get<UserSettingsModel>(
    `/ediagnostics/v1/settings/user/${encodeURIComponent(gmin)}`,
  );
  return data;
}

/** Persists the user's app settings. All values go in the query string (matches the backend). */
export async function saveUserSettings(
  gmin: string,
  params: { language: string; theme?: ThemeSetting },
): Promise<UserSettingsDto> {
  const { data } = await apiClient.post<UserSettingsDto>(
    `/ediagnostics/v1/settings/user/${encodeURIComponent(gmin)}`,
    null,
    { params },
  );
  return data;
}

/** Whether the user must (re)acknowledge the safety disclaimer, per the backend. */
export async function getDisclaimerAcknowledgement(
  gmin: string,
): Promise<DisclaimerAcknowledgement> {
  const { data } = await apiClient.get<DisclaimerAcknowledgement>(
    `/ediagnostics/v1/settings/user/${encodeURIComponent(gmin)}/disclaimer`,
  );
  return data;
}

/** Records the user's disclaimer acknowledgement (stamps the server-side timestamp). */
export async function saveDisclaimerAcknowledgement(
  gmin: string,
  acknowledgement: boolean,
): Promise<boolean> {
  const { data } = await apiClient.put<boolean>(
    `/ediagnostics/v1/settings/user/${encodeURIComponent(gmin)}/disclaimer`,
    null,
    { params: { acknowledgement } },
  );
  return data;
}

/** Seeds `user_info` on first login so UPDATE-only saves persist. */
export async function ensureUserSession(
  gmin: string,
  identity: { firstName?: string; lastName?: string; email?: string },
): Promise<void> {
  await apiClient.post(`/ediagnostics/v1/settings/user/${encodeURIComponent(gmin)}/session`, {
    firstName: identity.firstName ?? null,
    lastName: identity.lastName ?? null,
    email: identity.email ?? null,
  });
}

/** The caller's contact details, resolved server-side against Entra and GDL. */
export async function getUserProfile(gmin: string): Promise<UserProfile> {
  const { data } = await apiClient.get<UserProfile>(
    `/ediagnostics/v1/settings/user/${encodeURIComponent(gmin)}/profile`,
  );
  return data;
}

/** Dealer-editable profile slice: contact identity plus fallback dealership address. */
export interface UserProfileUpdate {
  firstName?: string;
  lastName?: string;
  email?: string;
  secondaryEmail?: string;
  phone: string;
  alternatePhone?: string;
  dealerName?: string;
  address?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
}

/** Persists the dealer's contact details (name/email/phone) and dealership address; returns the refreshed profile. */
export async function saveUserProfile(
  gmin: string,
  profile: UserProfileUpdate,
): Promise<UserProfile> {
  const { data } = await apiClient.put<UserProfile>(
    `/ediagnostics/v1/settings/user/${encodeURIComponent(gmin)}/profile`,
    profile,
  );
  return data;
}

/**
 * The layout engine this caller starts on and the engines they may pick. Resolved server-side from
 * the audience flags, so the explorer's switcher cannot disagree with what the renderer does.
 */
export interface LayoutEngineOptions {
  layoutMode: string;
  available: string[];
}

export async function getLayoutEngineOptions(): Promise<LayoutEngineOptions> {
  const { data } = await apiClient.get<LayoutEngineOptions>('/ediagnostics/v1/user/layout-mode');
  return data;
}

export async function getSchematicProperties(gmin: string): Promise<SchematicProperties> {
  const { data } = await apiClient.get<SchematicProperties>(
    `/api/v1/schematic-properties/user/${encodeURIComponent(gmin)}`,
  );
  return data;
}

export async function saveSchematicProperties(
  gmin: string,
  properties: SchematicProperties,
): Promise<SchematicProperties> {
  const { data } = await apiClient.put<SchematicProperties>(
    `/api/v1/schematic-properties/user/${encodeURIComponent(gmin)}`,
    { ...properties, userId: gmin },
  );
  return data;
}
